import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _extends from '@babel/runtime/helpers/esm/extends';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useRef, useState, useLayoutEffect, useEffect } from 'react';
import { V2CheckmarkIcon, CheckAltIcon } from '@paypalcorp/pp-react-icons';
import { LoadingSpinner } from '@paypalcorp/pp-react-loading-spinner';
import { Sheet } from '@paypalcorp/pp-react-sheet';
import { useTheme } from '@paypalcorp/pp-react-theme';
import clsx from 'clsx';
import '../__generated__/styles/selection-menu.css';

var styles = {"ppvx_selection-menu__container":"ppvx_selection-menu__container___1-9-1","ppvx_selection-menu":"ppvx_selection-menu___1-9-1","ppvx_menu-list--multiline":"ppvx_menu-list--multiline___1-9-1","ppvx_menu-list__item--thumbnail":"ppvx_menu-list__item--thumbnail___1-9-1","ppvx_menu-list__item":"ppvx_menu-list__item___1-9-1","keyboard-active":"keyboard-active___1-9-1","ppvx_selection-menu__item":"ppvx_selection-menu__item___1-9-1","ppvx_selection-menu--multiline":"ppvx_selection-menu--multiline___1-9-1","ppvx_selection-menu__item--thumbnail":"ppvx_selection-menu__item--thumbnail___1-9-1","ppvx_menu-list__item--hilite":"ppvx_menu-list__item--hilite___1-9-1","ppvx_selection-menu__item--text":"ppvx_selection-menu__item--text___1-9-1","ppvx_selection-menu__selected-icon":"ppvx_selection-menu__selected-icon___1-9-1","ppvx_selection-menu__item--text-with-svg-icon":"ppvx_selection-menu__item--text-with-svg-icon___1-9-1","ppvx_selection-menu__item--secondary-text":"ppvx_selection-menu__item--secondary-text___1-9-1","ppvx_selection-menu__item--thumbnail-image":"ppvx_selection-menu__item--thumbnail-image___1-9-1","ppvx_selection-menu__item--unselectable":"ppvx_selection-menu__item--unselectable___1-9-1","ppvx_selection-menu__item--hilite":"ppvx_selection-menu__item--hilite___1-9-1","ppvx--v2":"ppvx--v2___1-9-1","ppvx--v1":"ppvx--v1___1-9-1","ppvx_selection-menu__loading-container":"ppvx_selection-menu__loading-container___1-9-1"};

var selectionMenuClass = 'ppvx_selection-menu';
/* eslint-disable react/prop-types */

var SelectionMenuOption = function (_ref) {
  var _clsx;

  var menuName = _ref.menuName,
      primaryText = _ref.primaryText,
      secondaryText = _ref.secondaryText,
      decoration = _ref.decoration,
      optionId = _ref.id,
      value = _ref.value,
      isFocused = _ref.isFocused,
      isHilite = _ref.isHilite,
      isSelected = _ref.isSelected,
      className = _ref.className,
      unselectable = _ref.unselectable,
      isThemeV2 = _ref.isThemeV2,
      isLoadingOptions = _ref.isLoadingOptions,
      rest = _objectWithoutProperties(_ref, ["menuName", "primaryText", "secondaryText", "decoration", "id", "value", "isFocused", "isHilite", "isSelected", "className", "unselectable", "isThemeV2", "isLoadingOptions"]);

  var optionRef = useRef(null);
  var itemId = optionId || "".concat(menuName, "_item_").concat(value);
  var itemClasses = clsx(styles["".concat(selectionMenuClass, "__item")], (_clsx = {}, _defineProperty(_clsx, styles['ppvx_selection-menu__item--unselectable'], unselectable), _defineProperty(_clsx, styles["".concat(selectionMenuClass, "__item--hilite")], isHilite), _clsx), className);
  /* istanbul ignore next */

  useEffect(function () {
    if (optionRef && optionRef.current) {
      var option = optionRef.current;

      if (isFocused) {
        option.focus();
      }

      if (isFocused || isHilite) {
        if (option && option.parentElement && option.parentElement.parentElement) {
          var container = option.parentElement.parentElement;
          var boundingBox = container.getBoundingClientRect();
          var optionBox = option.getBoundingClientRect();

          if (optionBox.bottom > boundingBox.bottom || optionBox.top < boundingBox.top) {
            option.scrollIntoView({
              block: 'nearest',
              behavior: 'smooth'
            });
          }
        }
      }
    }
  }, [isHilite, isFocused]);
  var thumbNail = decoration ? /*#__PURE__*/React.createElement("div", {
    className: styles["".concat(selectionMenuClass, "__item--thumbnail")]
  }, decoration) : null;
  var itemTextClasses = clsx(styles["".concat(selectionMenuClass, "__item--text")], styles["".concat(selectionMenuClass, "__item--text-with-svg-icon")]);
  var selectedCheckMark = null;

  if (isSelected) {
    selectedCheckMark = isThemeV2 ? /*#__PURE__*/React.createElement(V2CheckmarkIcon, {
      className: styles["".concat(selectionMenuClass, "__selected-icon")]
    }) : /*#__PURE__*/React.createElement(CheckAltIcon, {
      className: styles["".concat(selectionMenuClass, "__selected-icon")]
    });
  }

  return /*#__PURE__*/React.createElement("li", _extends({
    className: itemClasses,
    id: itemId,
    role: "option",
    "aria-selected": isSelected,
    tabIndex: -1,
    "data-value": value,
    ref: optionRef
  }, rest), thumbNail, /*#__PURE__*/React.createElement("p", {
    className: itemTextClasses
  }, primaryText, secondaryText && /*#__PURE__*/React.createElement("span", {
    className: styles["".concat(selectionMenuClass, "__item--secondary-text")]
  }, secondaryText), selectedCheckMark));
};

var findValue = function (optionsList, value) {
  return optionsList.findIndex(function (option) {
    return value === option.value || value === option.primaryText;
  });
};
/*
 * Sigh... We need this if you want to use useState() from a "global" event handler
 * Otherwise you will get the old value of state and it won't ever appear to change.
 * This stores the state (to get refresh) and also stashes it into a ref, which
 * will always be up-to-date
 *
 * Love, BCC
 */


function useReferredState(initialValue) {
  var _React$useState = React.useState(initialValue),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      state = _React$useState2[0],
      setState = _React$useState2[1];

  var reference = React.useRef(state);
  return [reference, function setReferredState(value) {
    reference.current = value;
    setState(value);
  }];
}

var generateId = function () {
  return "selectionMenu_".concat(Math.random().toFixed(8).toString().replace('0.', ''));
};

var SelectionMenu = function (_ref2) {
  var _ref2$name = _ref2.name,
      name = _ref2$name === void 0 ? 'smenu' : _ref2$name,
      value = _ref2.value,
      className = _ref2.className,
      containerClassName = _ref2.containerClassName,
      itemClassName = _ref2.itemClassName,
      mobileHeaderClassName = _ref2.mobileHeaderClassName,
      sheetClassName = _ref2.sheetClassName,
      sheetWrapperClassName = _ref2.sheetWrapperClassName,
      _ref2$id = _ref2.id,
      id = _ref2$id === void 0 ? generateId() : _ref2$id,
      _ref2$initialItemFocu = _ref2.initialItemFocus,
      initialItemFocus = _ref2$initialItemFocu === void 0 ? true : _ref2$initialItemFocu,
      isOpen = _ref2.isOpen,
      ignoreKeyboard = _ref2.ignoreKeyboard,
      mobileTitle = _ref2.mobileTitle,
      _ref2$mobileDivider = _ref2.mobileDivider,
      mobileDivider = _ref2$mobileDivider === void 0 ? true : _ref2$mobileDivider,
      mobileHeaderContents = _ref2.mobileHeaderContents,
      outsideClickCheck = _ref2.outsideClickCheck,
      onClose = _ref2.onClose,
      onSelection = _ref2.onSelection,
      options = _ref2.options,
      _ref2$returnFocusToTr = _ref2.returnFocusToTrigger,
      returnFocusToTrigger = _ref2$returnFocusToTr === void 0 ? true : _ref2$returnFocusToTr,
      _ref2$selectedIndex = _ref2.selectedIndex,
      selectedIndex = _ref2$selectedIndex === void 0 ? 0 : _ref2$selectedIndex,
      _ref2$activeItem = _ref2.activeItem,
      activeItem = _ref2$activeItem === void 0 ? -1 : _ref2$activeItem,
      triggerElementId = _ref2.triggerElementId,
      theme = _ref2.theme,
      _ref2$rtl = _ref2.rtl,
      rtl = _ref2$rtl === void 0 ? false : _ref2$rtl,
      _ref2$isLoadingOption = _ref2.isLoadingOptions,
      isLoadingOptions = _ref2$isLoadingOption === void 0 ? false : _ref2$isLoadingOption,
      rest = _objectWithoutProperties(_ref2, ["name", "value", "className", "containerClassName", "itemClassName", "mobileHeaderClassName", "sheetClassName", "sheetWrapperClassName", "id", "initialItemFocus", "isOpen", "ignoreKeyboard", "mobileTitle", "mobileDivider", "mobileHeaderContents", "outsideClickCheck", "onClose", "onSelection", "options", "returnFocusToTrigger", "selectedIndex", "activeItem", "triggerElementId", "theme", "rtl", "isLoadingOptions"]);

  var wrapperRef = useRef();
  var menuRef = useRef();
  var loaderRef = useRef();
  var validateOptions = options && options.length;

  var _useReferredState = useReferredState(initialItemFocus ? selectedIndex : -1),
      _useReferredState2 = _slicedToArray(_useReferredState, 2),
      focusedIndexRef = _useReferredState2[0],
      setFocusedIndex = _useReferredState2[1];

  var _useState = useState(true),
      _useState2 = _slicedToArray(_useState, 2),
      keyboardActive = _useState2[0],
      setKeyboardActive = _useState2[1];

  var _theme = useTheme('SelectionMenu', theme);

  var isThemeV2 = _theme && _theme === 'v2';
  mobileDivider = isThemeV2 ? false : mobileDivider;
  var selectionMenuClasses = clsx(styles[selectionMenuClass], className, keyboardActive && styles['keyboard-active'], _defineProperty({}, styles['ppvx--v2'], isThemeV2)); // key codes

  var MAX_WIDTH = 1080;

  /* istanbul ignore next */
  var isMobile = typeof window !== 'undefined' ? window && window.innerWidth < 752 : false;

  var getRefCurrent = function (ref) {
    return (
      /* istanbul ignore next */
      ref && ref.current ? ref.current : null
    );
  };

  var calculateWidth = function () {
    var triggerWidth = triggerElementId && document.getElementById(triggerElementId).getBoundingClientRect().width;

    if (isLoadingOptions) {
      var loader = getRefCurrent(loaderRef);
      /* istanbul ignore next */

      loader.style.width = triggerWidth < MAX_WIDTH ? "".concat(triggerWidth, "px") : "".concat(MAX_WIDTH, "px");
    } else {
      var menu = getRefCurrent(menuRef);
      var menuWidth = menu && menu.getBoundingClientRect().width;
      /* istanbul ignore next */

      if (triggerWidth > 128 && triggerWidth < MAX_WIDTH && triggerWidth > menuWidth) {
        menu.style.width = "".concat(triggerWidth, "px");
      } else if (triggerWidth > MAX_WIDTH) {
        menu.style.width = "".concat(MAX_WIDTH, "px");
      }
    }
  };

  var setFocus = function (newIndex) {
    setFocusedIndex(newIndex);
  };

  var moveFocus = function (offset) {
    var nextIndex = Math.min(Math.max(focusedIndexRef.current + offset, 0), options.length - 1);
    setFocus(nextIndex);
  };

  var handleClickOutside = function (evt) {
    var wrapperCurrent = getRefCurrent(wrapperRef);
    var triggerElement = triggerElementId && document.getElementById(triggerElementId); // Trigger click is also handled here in React 17

    var isTriggerClicked = triggerElement && triggerElement.contains(evt.target); // istanbul ignore next

    if ((!outsideClickCheck || outsideClickCheck(evt)) && !isTriggerClicked) {
      if (wrapperCurrent && !wrapperCurrent.contains(evt.target)) {
        onClose(evt);
      }
    }
  };

  var handleClick = function (evt) {
    var target = evt.target;
    var index = findValue(options, target.dataset.value);
    setFocus(index);
    onSelection(index);
  };

  var handleKey = function (evt) {
    var focusedIndex = focusedIndexRef.current;
    /* istanbul ignore next */

    var keyCode = evt.key || evt.keyCode;

    switch (keyCode) {
      case 'ArrowUp':
      case 'Up':
      case 38:
        evt.preventDefault();
        setKeyboardActive(true);
        moveFocus(-1);
        break;

      case 'ArrowDown':
      case 'Down':
      case 40:
        evt.preventDefault();
        setKeyboardActive(true);
        moveFocus(1);
        break;

      case 'Enter':
      case 'ENTER':
      case 13:
        evt.preventDefault();
        onSelection(focusedIndex);
        break;

      case 'Tab':
      case 9:
      case 'Escape':
      case 'Esc':
      case 27:
        onClose(evt);
        document.getElementById(triggerElementId).focus();
        break;
    }
  };

  var handleKeyDown = function (evt) {
    handleKey(evt);
  };
  /* We need this method because we're trying to support index-based and
   * value-based operation at the same time. If we revert to only using
   * value-based, this code can get much simpler
   */


  var selectedValue = function getSelectedValue(currentValue, defaultValue) {
    if (currentValue !== undefined) {
      return currentValue;
    } // istanbul ignore next


    if (selectedIndex !== undefined && selectedIndex >= 0) {
      if (options && options[selectedIndex]) {
        if (options[selectedIndex].value !== undefined) {
          return options[selectedIndex].value;
        }
      }
    }

    return defaultValue;
  }(value);

  var renderImg = function (decoration) {
    return decoration && typeof decoration === 'string' ? /*#__PURE__*/React.createElement("img", {
      className: styles["".concat(selectionMenuClass, "__item--thumbnail-image")],
      src: decoration,
      role: "none"
    }) : decoration;
  };

  var focusedIndex = ignoreKeyboard ? -1 : focusedIndexRef.current;
  var optionList = isOpen && validateOptions ? options.map(function (option, idx) {
    // We have to test against false because non-existence means it's selectable
    var optionValue = option.hasOwnProperty('value') ? option.value : option.primaryText; // istanbul ignore next

    var isSelected = !option.unselectable && (selectedValue === undefined ? selectedIndex === idx : optionValue === selectedValue);
    var decoration = renderImg(option.decoration || option.imgSrc);
    return /*#__PURE__*/React.createElement(SelectionMenuOption, _extends({
      menuName: name
    }, option, {
      isThemeV2: isThemeV2,
      value: optionValue,
      isSelected: isSelected,
      isFocused: focusedIndex === idx,
      isHilite: activeItem === idx,
      className: itemClassName,
      decoration: decoration,
      key: "smenu_".concat(idx)
    }));
  }) : null;
  useLayoutEffect(function () {
    if (isOpen) {
      // Bind the event listener
      // @ts-ignore
      document.addEventListener('click', handleClickOutside);

      if (!ignoreKeyboard) {
        document.body.addEventListener('keydown', handleKeyDown);
      }

      if (!isMobile) {
        calculateWidth();
        setKeyboardActive(initialItemFocus); // istanbul ignore next

        if (focusedIndex < 0) {
          setFocus(0);
        }
      } else {
        // set keyboard focus active to false, on mobile we don't show the focus indicator
        setKeyboardActive(false);
      }
    }

    return function () {
      // @ts-ignore
      document.removeEventListener('click', handleClickOutside);

      if (!ignoreKeyboard) {
        document.body.removeEventListener('keydown', handleKeyDown);
      }

      setKeyboardActive(false);
    };
  }, [isOpen, isLoadingOptions]); //pass dir manually to fix styles in sheet rtl
  //set to null in ltr so the attribute is not added

  /* istanbul ignore next */

  var direction = rtl ? 'rtl' : null;
  var contentList = /*#__PURE__*/React.createElement("ul", _extends({
    className: selectionMenuClasses,
    id: id,
    role: "listbox",
    "aria-hidden": !isOpen,
    ref: menuRef,
    onClick: function onClick(evt) {
      return handleClick(evt);
    },
    dir: direction
  }, rest), optionList);
  var containerClasses = clsx(styles["".concat(selectionMenuClass, "__container")], containerClassName, _defineProperty({}, styles['ppvx--v2'], isThemeV2));
  var loadingSpinner = /*#__PURE__*/React.createElement("div", {
    ref: loaderRef,
    className: styles['ppvx_selection-menu__loading-container']
  }, /*#__PURE__*/React.createElement(LoadingSpinner, {
    screenReaderText: "loading",
    size: "lg",
    theme: _theme
  }));
  var content = isLoadingOptions ? loadingSpinner : contentList;

  var bodyWrapper = function () {
    if (isMobile) {
      // istanbul ignore next
      var isFixedHeight = mobileHeaderContents ? '90' : undefined;
      return /*#__PURE__*/React.createElement(Sheet, {
        isOpen: isOpen,
        onOpen: function onOpen() {
          return setFocus(0);
        },
        onClose: onClose,
        title: mobileTitle,
        theme: _theme,
        headerClassName: mobileHeaderClassName,
        headerContents: mobileHeaderContents,
        noPadding: true,
        noCloseButton: !mobileHeaderContents,
        divider: mobileDivider,
        mobileFixedHeight: isFixedHeight,
        className: sheetClassName,
        wrapperClassName: sheetWrapperClassName,
        returnFocusToTrigger: returnFocusToTrigger
      }, content);
    }

    return isOpen && /*#__PURE__*/React.createElement("div", {
      ref: wrapperRef,
      className: containerClasses
    }, content);
  };

  return bodyWrapper();
};

export { SelectionMenu };
